import { useState, useCallback, useEffect } from 'react';
import { loadCommissions, markCommissionPaid, bulkMarkCommissionsPaid } from '../services/commissionService';

/**
 * Custom hook for commission management
 * - Loads commissions based on user role
 * - Provides mark as paid functionality
 * - Auto-reloads after updates
 */
export function useCommissions(currentUser, showToast) {
  const [userCommissions, setUserCommissions] = useState([]);
  const [allCommissions, setAllCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);

  // Load commissions when user changes
  useEffect(() => {
    if (currentUser) {
      loadCommissionsData();
    }
  }, [currentUser]);

  /**
   * Load commissions from database
   */
  const loadCommissionsData = useCallback(async () => {
    if (!currentUser) return;
    
    setLoadingCommissions(true);
    try {
      const { userCommissions: userComm, allCommissions: allComm } = await loadCommissions(currentUser);
      
      if (currentUser.role === 'admin' || currentUser.role === 'sales_manager') {
        setAllCommissions(allComm);
      } else {
        setUserCommissions(userComm);
      }
    } catch (err) {
      console.error('❌ Error loading commissions:', err);
      if (showToast) {
        showToast('❌ فشل في تحميل العمولات', 'error');
      }
    } finally {
      setLoadingCommissions(false);
    }
  }, [currentUser]);

  /**
   * Mark single commission as paid
   */
  const handleMarkPaid = useCallback(async (commissionId, note = null) => {
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'sales_manager')) {
      return;
    }
    
    try {
      await markCommissionPaid(commissionId, currentUser.id, note);
      
      if (showToast) {
        showToast('✅ تم تحديد العمولة كمدفوعة');
      }
      
      // Reload commissions
      await loadCommissionsData();
    } catch (err) {
      console.error('❌ Error marking commission paid:', err);
      if (showToast) {
        showToast('❌ فشل في التحديث', 'error');
      }
    }
  }, [currentUser, showToast, loadCommissionsData]);

  /**
   * Bulk mark commissions as paid
   */
  const handleBulkMarkPaid = useCallback(async (commissionIds, note = null) => {
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'sales_manager')) {
      return;
    }
    
    try {
      await bulkMarkCommissionsPaid(commissionIds, currentUser.id, note);
      
      if (showToast) {
        showToast(`✅ تم تحديد ${commissionIds.length} عمولة كمدفوعة`);
      }
      
      // Reload commissions
      await loadCommissionsData();
    } catch (err) {
      console.error('❌ Error bulk marking commissions paid:', err);
      if (showToast) {
        showToast('❌ فشل في التحديث', 'error');
      }
    }
  }, [currentUser, showToast, loadCommissionsData]);

  return {
    userCommissions,
    allCommissions,
    loadingCommissions,
    loadCommissions: loadCommissionsData,
    markCommissionPaid: handleMarkPaid,
    bulkMarkCommissionsPaid: handleBulkMarkPaid,
  };
}
